import React, { useState } from "react";
import {
  Button,
  useDataProvider,
  useMutation,
  useNotify,
  useRefresh,
} from "react-admin";
import DoneIcon from "@material-ui/icons/Done";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import CustomDatePickerOutsideFormInput from "../../../components/CustomDatePickerOutsideFormInput";
import {
  IConfirmStudentRenewalButton,
  useStyles,
} from "./ConfirmStudentRenewalButton";

const getMinDate = (startDate: string) => {
  const academicYearStartDate = new Date(startDate);
  const today = new Date();

  // If academicYearStartDate is equals after today, use academicYearStartDate as the min date
  if (academicYearStartDate >= today) {
    return academicYearStartDate.toISOString().split("T")[0];
  }

  // Otherwise, use today's date as the min date
  return today.toISOString().split("T")[0];
};

const AcceptButton = ({
  id,
  studentId,
  academicYearId,
}: IConfirmStudentRenewalButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [loadingProcess, setLoadingProcess] = useState<boolean>(false);
  const [academicYear, setAcademicYear] = useState<IAcademicYear>();
  const [selectedDate, setSelectedDate] = useState<string>(null);

  const classes = useStyles();
  const refresh = useRefresh();
  const notify = useNotify();
  const provider = useDataProvider();

  const handleClickButton = (academicYearId: number) => {
    setLoadingProcess(true);
    provider
      .getOne("academicYears", {
        id: academicYearId,
      })
      .then(({ data }) => {
        if (new Date() > new Date(data.earlyRegistrationEndDate)) {
          setAcademicYear(data as IAcademicYear);
          handleOpenDialog();
        } else {
          handleAcceptStudentRenewal();
        }

        setLoadingProcess(false);
      });
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [handleAcceptStudentRenewal, { loading }] = useMutation(
    {
      type: "update",
      resource: `studentRenewals`,
      payload: {
        id: id,
        data: {
          isAcceptRenewal: true,
          studentId: studentId,
          academicYearId: academicYearId,
          scheduleStartDate: selectedDate,
        },
      },
    },
    {
      onSuccess: ({ data }) => {
        notify(`Successfully accept student renewal.`, "info");
        refresh();
      },
      onFailure: (error) =>
        notify(
          `Failed accept student renewal, error: ${error.message}`,
          "warning"
        ),
    }
  );

  return (
    <>
      <Button
        onClick={() => handleClickButton(academicYearId)}
        color="primary"
        label={loading || loadingProcess ? "Please wait..." : "Accept"}
        autoFocus
      >
        <DoneIcon />
      </Button>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialogSetDate}
        maxWidth="sm"
      >
        {academicYear && (
          <>
            <DialogTitle id="alert-dialog-title">
              Academic Year {academicYear?.name} Information
            </DialogTitle>
            <DialogContent>
              <DialogContentText color="initial">
                <Typography variant="body1" gutterBottom>
                  The Early Registration period has been passed.
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Please accept the renewal with select a date to set as the
                  student's schedule.
                </Typography>
                <CustomDatePickerOutsideFormInput
                  name="scheduleStartDate"
                  label="Schedule Start Date"
                  min={getMinDate(academicYear.startDate)}
                  max={academicYear.endDate}
                  parentCallback={(date: string) => {
                    setSelectedDate(date);
                  }}
                  isWeekDays
                />
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleClose}
                color="secondary"
                label="Back"
                style={{ backgroundColor: "#9e9e9e" }}
              />
              <Button
                onClick={handleAcceptStudentRenewal}
                color="primary"
                label={loading || loadingProcess ? "Please wait..." : "Accept"}
                disabled={selectedDate === null}
                autoFocus
              >
                <DoneIcon />
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default AcceptButton;
